import { Controller } from "@hotwired/stimulus"

// Toggles visibility of the Meeting Link and In-Studio Location fields based on Class Location selection
// Also updates the preview logo based on selected class type
export default class extends Controller {
  static targets = [
    "zoomUrl",
    "zoomAvailabilityChecking",
    "meetingLinkInput",
    "meetingLinkHint",
    "inStudioLocation",
    "recurringDays",
    "studentField",
    "classLogo",
    "startsAtDisplay",
    "startsAtHidden",
    "endsOnDisplay",
    "endsOnHidden",
    "endsOnPicker",
    "startsAtPicker"
  ]
  static values = {
    online: { type: String, default: "online" },
    inStudio: { type: String, default: "in_studio" },
    hybrid: { type: String, default: "hybrid" },
    availabilityUrl: String,
    studioClassId: String
  }

  connect() {
    this.updateVisibility()
    this.updateRecurringState()
    this.updateServiceVisibility()
    this.updateClassLogo()
    this.updateEndsOnMin()
    const form = this.element.querySelector("form")
    if (form) form.addEventListener("submit", this.convertDatesForSubmit.bind(this))
  }

  updateRecurringState() {
    if (!this.hasRecurringDaysTarget) return
    const checkbox = this.element.querySelector("#recurring_enabled")
    if (checkbox) {
      this.recurringDaysTarget.querySelectorAll(".recurring-day-cb").forEach((cb) => {
        cb.disabled = !checkbox.checked
      })

    }
  }

  change() {
    this.updateVisibility()
  }

  updateServiceVisibility() {
    const serviceSelect = this.element.querySelector('select[name="studio_class[service_type]"]')
    if (!serviceSelect) return
    const service = serviceSelect.value

    // Show/hide student field
    if (this.hasStudentFieldTarget) {
      const isPersonal = service === "personal"
      this.studentFieldTarget.classList.toggle("hidden", !isPersonal)
      const studentSelect = this.studentFieldTarget.querySelector("select")
      if (studentSelect) {
        if (isPersonal) studentSelect.removeAttribute("disabled")
        else studentSelect.setAttribute("disabled", "disabled")
      }
    }

    // Filter class category options by selected service
    const categorySelect = this.element.querySelector('select[name="studio_class[class_category]"]')
    if (categorySelect && service) {
      categorySelect.querySelectorAll("option").forEach(option => {
        if (!option.value) return
        const allowed = (option.dataset.service || "").split(" ")
        option.hidden = !allowed.includes(service)
      })
      // Reset selection if current choice is now hidden
      const current = categorySelect.querySelector("option:checked")
      if (current && current.hidden) categorySelect.value = ""

      // Personal Classes: auto-select the only valid class type ("Personal Class")
      if (service === "personal") categorySelect.value = "personal"
    }

    // Personal Classes: default location to "Online classes"
    if (service === "personal") {
      const onlineRadio = this.element.querySelector('input[name="studio_class[location_type]"][value="online"]')
      if (onlineRadio && !onlineRadio.checked) {
        onlineRadio.checked = true
        this.updateVisibility()
      }
    }

    this.updateClassLogo()
  }

  toggleRecurring() {
    this.updateRecurringState()

    // A recurring series is validated as a whole pattern server-side on
    // submit (StudioClass#recurring_series_hosts_available) — the live
    // single-slot pre-check below only ever looks at the raw anchor
    // date/time, so it must not force the field open (and required) just
    // because that one arbitrary anchor slot happens to be busy.
    if (this.isRecurringEnabled()) {
      if (this.hasZoomUrlTarget) this.toggleTarget(this.zoomUrlTarget, false)
      if (this.hasZoomAvailabilityCheckingTarget) this.toggleTarget(this.zoomAvailabilityCheckingTarget, false)
    } else {
      this.scheduleAvailabilityCheck()
    }
  }

  isRecurringEnabled() {
    const checkbox = this.element.querySelector("#recurring_enabled")
    return !!(checkbox && checkbox.checked)
  }

  updateClassLogo() {
    if (!this.hasClassLogoTarget) return

    const select = this.element.querySelector('select[name="studio_class[class_category]"]')
    if (!select) return

    const value = select.value

    this.classLogoTargets.forEach((logoEl) => {
      const category = logoEl.dataset.classFormCategory
      const shouldShow = value && category === value
      logoEl.classList.toggle("hidden", !shouldShow)
    })
  }

  updateVisibility() {
    const selected = this.element.querySelector('input[name="studio_class[location_type]"]:checked')
    if (!selected) return

    const value = selected.value
    const showZoom = value === this.onlineValue || value === this.hybridValue
    const showInStudio = value === this.inStudioValue || value === this.hybridValue

    if (this.hasInStudioLocationTarget) this.toggleTarget(this.inStudioLocationTarget, showInStudio)

    if (showZoom) {
      this.scheduleAvailabilityCheck()
    } else {
      if (this.hasZoomUrlTarget) this.toggleTarget(this.zoomUrlTarget, false)
      if (this.hasZoomAvailabilityCheckingTarget) this.toggleTarget(this.zoomAvailabilityCheckingTarget, false)
    }
  }

  syncMeetingLinkHint() {
    if (!this.hasMeetingLinkHintTarget || !this.hasMeetingLinkInputTarget) return
    const value = this.meetingLinkInputTarget.value.trim()
    this.meetingLinkHintTarget.classList.toggle("hidden", value.length > 0)

    if (value.length > 0 && !this.isValidMeetingUrl(value)) {
      this.meetingLinkInputTarget.setCustomValidity(
        "This doesn't look like a valid link. Make sure to paste the full meeting URL (e.g. https://zoom.us/j/...)."
      )
    } else {
      this.meetingLinkInputTarget.setCustomValidity("")
    }
  }

  isValidMeetingUrl(value) {
    try {
      const u = new URL(value)
      return (u.protocol === "http:" || u.protocol === "https:") && u.hostname.includes(".")
    } catch {
      return false
    }
  }

  scheduleAvailabilityCheck() {
    clearTimeout(this._availabilityTimeout)
    this._availabilityTimeout = setTimeout(() => this.checkAvailability(), 400)
  }

  async checkAvailability() {
    if (!this.hasZoomUrlTarget || !this.hasAvailabilityUrlValue) return
    if (this.isRecurringEnabled()) return

    const startsAt = this.hasStartsAtHiddenTarget ? this.startsAtHiddenTarget.value : null
    const durationInput = this.element.querySelector('input[name="studio_class[duration_minutes]"]')
    const duration = durationInput ? parseInt(durationInput.value, 10) : NaN
    if (!startsAt || !duration || duration <= 0) return

    const requestId = (this._availabilityRequestId = (this._availabilityRequestId || 0) + 1)
    if (this.hasZoomAvailabilityCheckingTarget) this.toggleTarget(this.zoomAvailabilityCheckingTarget, true)

    const url = new URL(this.availabilityUrlValue, window.location.origin)
    url.searchParams.set("starts_at", startsAt)
    url.searchParams.set("duration_minutes", duration)
    if (this.hasStudioClassIdValue && this.studioClassIdValue) {
      url.searchParams.set("exclude_id", this.studioClassIdValue)
    }

    try {
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      if (requestId !== this._availabilityRequestId) return
      if (!response.ok) throw new Error("availability check failed")
      const data = await response.json()
      this.toggleTarget(this.zoomUrlTarget, !data.available)
      this.syncMeetingLinkHint()
    } catch (e) {
      // Fail open: a flaky check should never force the field open on its
      // own — save-time validation still requires it if genuinely needed.
      if (requestId === this._availabilityRequestId) this.toggleTarget(this.zoomUrlTarget, false)
    } finally {
      if (requestId === this._availabilityRequestId && this.hasZoomAvailabilityCheckingTarget) {
        this.toggleTarget(this.zoomAvailabilityCheckingTarget, false)
      }
    }
  }

  updateEndsOnMin() {
    if (this.hasStartsAtDisplayTarget && this.hasStartsAtHiddenTarget) {
      const iso = this.parseDDMMYYDateTime(this.startsAtDisplayTarget.value.trim())
      if (iso) {
        this.startsAtHiddenTarget.value = iso
        if (this.hasEndsOnPickerTarget) this.endsOnPickerTarget.min = iso.split("T")[0]
      }
    }
    this.validateEndsOn()
    this.scheduleAvailabilityCheck()
  }

  validateEndsOn() {
    if (!this.hasEndsOnDisplayTarget) return
    const endsVal = this.endsOnDisplayTarget.value.trim()
    if (!endsVal) return
    const endsIso = this.parseDDMMYYDate(endsVal)
    if (!endsIso) return
    const startsIso = this.hasStartsAtHiddenTarget ? this.startsAtHiddenTarget.value : null
    if (startsIso && endsIso < startsIso.split("T")[0]) {
      this.endsOnDisplayTarget.setCustomValidity("End date cannot be before start date")
    } else {
      this.endsOnDisplayTarget.setCustomValidity("")
    }
  }

  convertDatesForSubmit(event) {
    if (this.hasStartsAtDisplayTarget && this.hasStartsAtHiddenTarget) {
      const val = this.startsAtDisplayTarget.value.trim()
      const iso = this.parseDDMMYYDateTime(val)
      if (iso) {
        this.startsAtHiddenTarget.value = iso
        this.startsAtDisplayTarget.setCustomValidity("")
      } else if (val) {
        event.preventDefault()
        this.startsAtDisplayTarget.setCustomValidity("Please enter date as dd/mm/yy hh:mm (e.g. 25/06/26 09:00)")
        this.startsAtDisplayTarget.reportValidity()
        return
      }
    }
    if (this.hasEndsOnDisplayTarget && this.hasEndsOnHiddenTarget) {
      const val = this.endsOnDisplayTarget.value.trim()
      const iso = this.parseDDMMYYDate(val)
      if (iso) {
        this.endsOnHiddenTarget.value = iso
        this.endsOnDisplayTarget.setCustomValidity("")
      } else if (val) {
        event.preventDefault()
        this.endsOnDisplayTarget.setCustomValidity("Please enter date as dd/mm/yy (e.g. 25/06/26)")
        this.endsOnDisplayTarget.reportValidity()
        return
      }
    }

    // Cross-field: end date must not be before start date
    const startsIso = this.hasStartsAtHiddenTarget ? this.startsAtHiddenTarget.value : null
    const endsIso = this.hasEndsOnHiddenTarget ? this.endsOnHiddenTarget.value : null
    if (startsIso && endsIso && endsIso < startsIso.split("T")[0]) {
      event.preventDefault()
      this.endsOnDisplayTarget.setCustomValidity("End date cannot be before start date")
      this.endsOnDisplayTarget.reportValidity()
    }
  }

  openStartsAtPicker() {
    if (!this.hasStartsAtPickerTarget) return
    const iso = this.parseDDMMYYDateTime(this.startsAtDisplayTarget.value.trim())
    if (iso) this.startsAtPickerTarget.value = iso
    if (this.startsAtPickerTarget.showPicker) {
      this.startsAtPickerTarget.showPicker()
    } else {
      this.startsAtPickerTarget.click()
    }
  }

  startsAtPickerChanged() {
    const iso = this.startsAtPickerTarget.value
    if (!iso) return
    const [datePart, timePart] = iso.split("T")
    const [y, m, d] = datePart.split("-")
    this.startsAtDisplayTarget.value = `${d}/${m}/${y.slice(2)} ${timePart}`
    this.startsAtDisplayTarget.setCustomValidity("")
    if (this.hasStartsAtHiddenTarget) this.startsAtHiddenTarget.value = iso
    this.scheduleAvailabilityCheck()
  }

  openEndsOnPicker() {
    if (!this.hasEndsOnPickerTarget) return
    const iso = this.parseDDMMYYDate(this.endsOnDisplayTarget.value.trim())
    if (iso) this.endsOnPickerTarget.value = iso
    if (this.endsOnPickerTarget.showPicker) {
      this.endsOnPickerTarget.showPicker()
    } else {
      this.endsOnPickerTarget.click()
    }
  }

  endsOnPickerChanged() {
    const iso = this.endsOnPickerTarget.value
    if (!iso) return
    const [y, m, d] = iso.split("-")
    this.endsOnDisplayTarget.value = `${d}/${m}/${y.slice(2)}`
    if (this.hasEndsOnHiddenTarget) this.endsOnHiddenTarget.value = iso
    this.validateEndsOn()
  }

  parseDDMMYYDate(str) {
    const match = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/)
    if (!match) return null
    let [, d, m, y] = match
    if (y.length === 2) y = `20${y}`
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`
  }

  parseDDMMYYDateTime(str) {
    const match = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})\s+(\d{1,2}):(\d{2})$/)
    if (!match) return null
    let [, d, m, y, h, min] = match
    if (y.length === 2) y = `20${y}`
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}T${h.padStart(2, "0")}:${min}`
  }

  toggleTarget(target, show) {
    if (!target) return
    if (show) {
      target.classList.remove("hidden")
      target.querySelector("select, input")?.removeAttribute("disabled")
    } else {
      target.classList.add("hidden")
      const input = target.querySelector("select, input")
      if (input) {
        input.setAttribute("disabled", "disabled")
      }
    }
  }
}
